/**
 * AvAContextDisplay — Feb 2026
 * ============================
 *
 * Sits in the desktop chat layout, directly below the AvA portrait and
 * the Text / Voice / Both interaction-mode selector. It surfaces any
 * "reference content" implied by AvA's most recent reply:
 *
 *   • a URL  → renders a link-preview card (favicon + hostname + title)
 *   • an image URL → renders the image inline
 *   • neither → empty hint copy that disappears when content arrives
 *
 * Toolbar (top-right of the box):
 *   • Expand   — full-screen lightbox (image stretched / iframe for URL)
 *   • Save     — POST /api/chat/context/save → drops into the teen's
 *                personal Reference Library
 *   • Download — native download (image) or copy URL fallback
 *   • Share    — Web Share API with copy-to-clipboard fallback
 *   • Delete   — clears the current item from the box (UI-only;
 *                the source message in the chat log is untouched)
 *
 * The component is pure UI + a single API call for "Save"; everything
 * else (URL detection, link card) runs entirely client-side from the
 * `messages` array the parent already manages.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Maximize2, Save, Download, Share2, Trash2, ImageIcon, ExternalLink,
  Sparkles, X, Loader2, Check, History, BookMarked,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const IMG_EXT_RE = /\.(?:png|jpe?g|gif|webp|avif|svg)(?:\?.*)?$/i;

const hostnameOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

const AvAContextDisplay = ({ messages, avaName }) => {
  // Build the full reference timeline from every AvA message in the
  // current session. Newest first; cap at 10 so the history dropdown
  // stays usable. Each AvA message can contribute multiple URLs
  // (e.g. "see this and this"); we honour insertion order so the
  // freshest reference wins the main slot.
  const allContexts = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    const out = [];
    const seen = new Set();
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (!m || m.role !== 'ava' || typeof m.content !== 'string') continue;
      // Pull ALL urls from this message, in order.
      const matches = m.content.match(/https?:\/\/[^\s)]+/gi) || [];
      for (const raw of matches) {
        const url = raw.replace(/[.,;!?)]+$/, '');
        if (seen.has(url)) continue;
        seen.add(url);
        out.push({
          url,
          kind: IMG_EXT_RE.test(url) ? 'image' : 'link',
          messageId: m.id,
          sourceText: m.content,
          restored: m.restored === true,
        });
        if (out.length >= 10) return out;
      }
    }
    return out;
  }, [messages]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [dismissedKey, setDismissedKey] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedBadge, setSavedBadge] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Whenever a NEW reference arrives at the top, snap the active slot
  // back to 0 so the user immediately sees what AvA just shared.
  const headKey = allContexts[0]?.url || null;
  useEffect(() => {
    setActiveIdx(0);
    setDismissedKey(null);
    setSaved(false);
    setFeedback('');
  }, [headKey]);

  // Feb 2026 — Subtle 200ms fade on lightbox open AND close.
  //   • `expanded`         → user intent (true = wants lightbox visible)
  //   • `lightboxMounted`  → portal is in the DOM (kept true during exit fade)
  //   • `lightboxVisible`  → CSS opacity target (0 → 1 on enter, 1 → 0 on exit)
  // Enter: mount → next frame flip opacity to 1.
  // Exit:  flip opacity to 0 → after 200ms unmount the portal.
  const [lightboxMounted, setLightboxMounted] = useState(false);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  useEffect(() => {
    if (expanded) {
      setLightboxMounted(true);
      const raf = requestAnimationFrame(() => setLightboxVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setLightboxVisible(false);
    const t = setTimeout(() => setLightboxMounted(false), 200);
    return () => clearTimeout(t);
  }, [expanded]);

  // Feb 2026 — Prefetch warm-up. As soon as an image reference lands at
  // the head slot, kick off a hidden `new Image()` load so the browser
  // cache is warm by the time the teen expands (or Rebecca auto-expands)
  // the lightbox. Fires for ALL AvAs — cheap, and the auto-lightbox path
  // for Rebecca especially benefits on slow classroom Wi-Fi. Tracked
  // per-URL so we never double-fetch.
  const prefetchedUrlsRef = useRef(new Set());
  useEffect(() => {
    const head = allContexts[0];
    if (!head || head.kind !== 'image') return;
    if (prefetchedUrlsRef.current.has(head.url)) return;
    prefetchedUrlsRef.current.add(head.url);
    const warm = new Image();
    warm.decoding = 'async';
    warm.src = head.url;
  }, [headKey, allContexts]);

  // Feb 2026 — Rebecca (fixed-anchor STEM tutor) auto-expands the lightbox
  // the FIRST time she surfaces an image, so a teen asking "show me the
  // Milky Way" gets an immediate full-screen reveal instead of having to
  // hunt for the Expand button. Tracked per-URL in a ref so closing the
  // lightbox is honoured — we never re-open the same reference twice.
  const autoExpandedUrlsRef = useRef(new Set());
  const isRebecca = (avaName || '').trim().toLowerCase() === 'rebecca';
  useEffect(() => {
    if (!isRebecca) return;
    const head = allContexts[0];
    if (!head || head.kind !== 'image') return;
    if (autoExpandedUrlsRef.current.has(head.url)) return;
    autoExpandedUrlsRef.current.add(head.url);
    // Restored-history references must never auto-pop the lightbox on
    // page load — only images Rebecca shares live in this session do.
    if (head.restored) return;
    setExpanded(true);
  }, [headKey, isRebecca, allContexts]);

  const latestContext = allContexts[activeIdx] || null;
  const visible = latestContext && latestContext.url !== dismissedKey;

  const handleDelete = () => {
    if (!latestContext) return;
    setDismissedKey(latestContext.url);
    setExpanded(false);
    setHistoryOpen(false);
  };

  const handleSave = async () => {
    if (!latestContext || saving || saved) return;
    setSaving(true);
    try {
      const data = await apiClient.request('/api/chat/context/save', {
        method: 'POST',
        body: JSON.stringify({
          url: latestContext.url,
          kind: latestContext.kind,
          source_text: (latestContext.sourceText || '').slice(0, 800),
          ava_name: avaName || '',
        }),
      });
      if (data?.success) {
        setSaved(true);
        // Feb 2026 — special-case: instead of a fleeting text feedback,
        // show a linkable "Added to your Library →" toast that routes
        // the teen to their personal Reference Library. Longer dwell
        // (5s) so the link is actually tappable.
        setSavedBadge(true);
        setTimeout(() => setSavedBadge(false), 5000);
      } else {
        setFeedback('Save failed — try again.');
      }
    } catch (e) {
      setFeedback(e?.message || 'Save failed — try again.');
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(''), 2500);
    }
  };

  const handleDownload = async () => {
    if (!latestContext) return;
    if (latestContext.kind === 'image') {
      try {
        const resp = await fetch(latestContext.url, { mode: 'cors' });
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = (latestContext.url.split('/').pop() || 'image').split('?')[0] || 'image';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        setFeedback('Downloaded');
      } catch {
        // CORS-blocked → fall back to opening in a new tab.
        window.open(latestContext.url, '_blank', 'noopener,noreferrer');
        setFeedback('Opened in a new tab');
      }
    } else {
      try {
        await navigator.clipboard.writeText(latestContext.url);
        setFeedback('Link copied');
      } catch {
        window.open(latestContext.url, '_blank', 'noopener,noreferrer');
        setFeedback('Opened in a new tab');
      }
    }
    setTimeout(() => setFeedback(''), 2500);
  };

  const handleShare = async () => {
    if (!latestContext) return;
    const shareData = {
      title: `Shared by ${avaName || 'HiBFF'}`,
      text: 'Take a look at this:',
      url: latestContext.url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setFeedback('Shared');
      } catch {
        // user cancelled — silent
      }
    } else {
      try {
        await navigator.clipboard.writeText(latestContext.url);
        setFeedback('Link copied to share');
      } catch {
        setFeedback('Could not share');
      }
    }
    setTimeout(() => setFeedback(''), 2500);
  };

  const renderToolbar = (stacked = false) => (
    <div className={`flex items-center ${stacked ? 'gap-1' : 'gap-0.5'}`} data-testid="ava-context-toolbar">
      <ToolButton
        onClick={() => { setHistoryOpen((v) => !v); setExpanded(false); }}
        label={`History (${allContexts.length})`}
        testid="ava-context-history"
        icon={<History size={14} />}
        accent={historyOpen ? '#7B4FD8' : undefined}
        disabled={allContexts.length === 0}
      />
      <ToolButton
        onClick={() => setExpanded(true)}
        label="Expand"
        testid="ava-context-expand"
        icon={<Maximize2 size={14} />}
      />
      <ToolButton
        onClick={handleSave}
        label={saved ? 'Saved' : 'Save'}
        testid="ava-context-save"
        icon={saving ? <Loader2 size={14} className="animate-spin" /> : (saved ? <Check size={14} /> : <Save size={14} />)}
        accent={saved ? '#16A34A' : undefined}
      />
      <ToolButton
        onClick={handleDownload}
        label="Download"
        testid="ava-context-download"
        icon={<Download size={14} />}
      />
      <ToolButton
        onClick={handleShare}
        label="Share"
        testid="ava-context-share"
        icon={<Share2 size={14} />}
      />
      <ToolButton
        onClick={handleDelete}
        label="Remove"
        testid="ava-context-delete"
        icon={<Trash2 size={14} />}
        accent="#DC2626"
      />
    </div>
  );

  return (
    <div className="mt-4 w-full relative" data-testid="ava-context-display">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles size={13} style={{ color: '#7B4FD8' }} />
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
            Reference
          </span>
          {allContexts.length > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#EDE9FE', color: '#5B21B6' }}
              data-testid="ava-context-count"
            >
              {Math.min(activeIdx + 1, allContexts.length)}/{allContexts.length}
            </span>
          )}
        </div>
        {(visible || allContexts.length > 0) && renderToolbar()}
      </div>

      {/* Last-10 history dropdown panel — anchored to the right edge so
          it doesn't shove other UI around. Scroll-up/down within the
          panel (`overflow-y-auto`) since we hard-cap at 10. */}
      {historyOpen && allContexts.length > 0 && (
        <div
          className="absolute right-0 top-7 z-30 w-[320px] max-h-[320px] overflow-y-auto rounded-xl border border-purple-200 bg-white shadow-xl"
          data-testid="ava-context-history-panel"
          onMouseLeave={() => setHistoryOpen(false)}
        >
          <div className="px-3 py-2 border-b border-purple-100 sticky top-0 bg-white/95 backdrop-blur z-10 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700">
              Last {allContexts.length} reference{allContexts.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={() => setHistoryOpen(false)}
              className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100"
              aria-label="Close history"
              data-testid="ava-context-history-close"
            >
              <X size={12} />
            </button>
          </div>
          <ul className="py-1">
            {allContexts.map((c, idx) => (
              <li key={`${c.url}-${idx}`}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveIdx(idx);
                    setDismissedKey(null);
                    setHistoryOpen(false);
                  }}
                  className={`w-full flex items-start gap-2.5 px-3 py-2 text-left transition ${
                    idx === activeIdx ? 'bg-purple-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`ava-context-history-item-${idx}`}
                >
                  {c.kind === 'image' ? (
                    <img
                      src={c.url}
                      alt=""
                      className="w-10 h-10 rounded-md object-cover flex-shrink-0 border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span
                      className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ background: idx === activeIdx ? '#7B4FD8' : '#EDE9FE', color: idx === activeIdx ? 'white' : '#5B21B6' }}
                    >
                      <ExternalLink size={15} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700">
                      {hostnameOf(c.url)}
                    </div>
                    <div className="text-xs text-gray-800 leading-snug truncate font-mono">
                      {c.url}
                    </div>
                  </div>
                  {idx === activeIdx && (
                    <Check size={14} style={{ color: '#7B4FD8' }} className="flex-shrink-0 mt-1" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className="rounded-2xl border-2 transition-colors"
        style={{
          borderColor: visible ? '#7B4FD8' : '#C7D2FE',
          background: visible ? '#FAF5FF' : '#F5F8FF',
          minHeight: '180px',
        }}
        data-testid="ava-context-box"
      >
        {visible ? (
          <ContextBody ctx={latestContext} />
        ) : (
          <EmptyState avaName={avaName} />
        )}
      </div>

      {feedback && (
        <div
          className="mt-1.5 text-[11px] font-semibold"
          style={{ color: feedback.toLowerCase().includes('fail') ? '#B91C1C' : '#15803D' }}
          data-testid="ava-context-feedback"
        >
          {feedback}
        </div>
      )}

      {/* Feb 2026 — "Added to your Library →" linkable toast fires only
          after a successful Save. Distinct from the transient feedback
          line above so error/download/share flows are untouched. */}
      {savedBadge && (
        <Link
          to="/my-references"
          className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100 hover:border-emerald-300 transition"
          data-testid="ava-context-saved-badge"
        >
          <BookMarked size={12} />
          Added to your Library
          <span aria-hidden>→</span>
        </Link>
      )}

      {/* Fullscreen lightbox */}
      {lightboxMounted && visible && createPortal(
        <div
          className="fixed inset-0 z-[9550] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
          data-testid="ava-context-lightbox"
          style={{
            opacity: lightboxVisible ? 1 : 0,
            transition: 'opacity 200ms ease-out',
          }}
        >
          <div
            className="relative w-full max-w-5xl max-h-[90vh] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 text-white">
              <div className="flex items-center gap-2 min-w-0">
                {latestContext.kind === 'image' ? <ImageIcon size={14} /> : <ExternalLink size={14} />}
                <span className="text-xs font-semibold truncate">{hostnameOf(latestContext.url)}</span>
              </div>
              <div className="flex items-center gap-2">
                {renderToolbar(true)}
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                  aria-label="Close"
                  data-testid="ava-context-lightbox-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-black flex items-center justify-center">
              {latestContext.kind === 'image' ? (
                <img
                  src={latestContext.url}
                  alt="reference"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                <iframe
                  src={latestContext.url}
                  title="reference"
                  className="w-full h-[80vh] bg-white"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

const ToolButton = ({ onClick, icon, label, testid, accent, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    disabled={disabled}
    data-testid={testid}
    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-600 hover:bg-purple-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
    style={accent ? { color: accent } : undefined}
  >
    {icon}
  </button>
);

const ContextBody = ({ ctx }) => (
  ctx.kind === 'image' ? (
    <div className="w-full h-full p-2 flex items-center justify-center">
      <img
        src={ctx.url}
        alt="reference"
        className="max-w-full max-h-[260px] object-contain rounded-lg"
        data-testid="ava-context-image"
      />
    </div>
  ) : (
    <a
      href={ctx.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-4 hover:bg-white/60 transition rounded-2xl"
      data-testid="ava-context-link"
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: '#7B4FD8', color: 'white' }}
      >
        <ExternalLink size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700 mb-0.5">
          {hostnameOf(ctx.url)}
        </div>
        <div className="text-sm font-semibold text-gray-900 leading-snug break-words">
          {ctx.url}
        </div>
        <div className="text-[11px] text-gray-500 mt-1">Tap to open in a new tab</div>
      </div>
    </a>
  )
);

const EmptyState = ({ avaName }) => (
  <div className="h-full min-h-[180px] flex items-center justify-center p-5 text-center">
    <div>
      <div className="mx-auto w-9 h-9 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
        <ImageIcon size={16} className="text-purple-400" />
      </div>
      <div className="text-xs font-semibold text-gray-700 mb-1">Reference content</div>
      <div className="text-[11px] text-gray-500 leading-snug max-w-[220px] mx-auto">
        Anything {avaName || 'your AvA'} shares — links, images, examples — will appear here so you can revisit, save, or share it.
      </div>
    </div>
  </div>
);

export default AvAContextDisplay;
